import http from '@/lib/http'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { MedRequest } from '@/types/data'
import { useState } from 'react'

type HistoryData = {
  items: MedRequest[]
  total: number
}

const FETCH_LIMIT = 200

const getHistoryQueryKey = () => {
  return ['allMedRequestHistory', 'v2Only'] as const
}

const fetchMedRequestHistoryData = (pageSize: number, offset: number): Promise<MedRequest[]> => {
  return http.get(`/prescriber/med-requests/history/v2?limit=${pageSize}&offset=${offset}`)
}

const fetchMedRequestHistoryCount = (): Promise<number> => {
  return http.get('/prescriber/med-requests/history/v2/count')
}

const getSortTimestamp = (r: MedRequest): string | null => r.lastActionTakenAt ?? r.lastUpdateOn

const fetchHistoryData = async (): Promise<HistoryData> => {
  const items = await fetchMedRequestHistoryData(FETCH_LIMIT, 0)
  items.sort((a, b) => {
    const tsA = getSortTimestamp(a)
    const tsB = getSortTimestamp(b)
    const dateA = tsA ? new Date(tsA).getTime() : -Infinity
    const dateB = tsB ? new Date(tsB).getTime() : -Infinity
    return dateB - dateA
  })
  return { items, total: items.length }
}

export const useMedRequestHistoryCount = () => {
  return useSuspenseQuery({
    queryKey: ['medRequestHistoryCount'],
    queryFn: () => fetchMedRequestHistoryCount().then(count => Math.min(count, FETCH_LIMIT))
  })
}

/**
 * Hook to fetch the med request history. The whole window is fetched up front and
 * paginated client-side.
 */
const useMedRequestHistory = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const currentDataOffset = pagination.pageIndex * pagination.pageSize

  const query = useSuspenseQuery({
    queryKey: getHistoryQueryKey(),
    queryFn: fetchHistoryData
  })

  const pageData = query.data.items.slice(currentDataOffset, currentDataOffset + pagination.pageSize)

  return {
    query: { ...query, data: pageData },
    total: query.data.total,
    pagination,
    setPagination,
    currentDataOffset
  }
}

export default useMedRequestHistory
